import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Settings as SettingsIcon, 
  User,
  Shield,
  Moon,
  Sun,
  Palette,
  Key,
  Smartphone,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Copy,
  Building2,
  Database,
  Zap,
  Map
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';



export default function Settings() {
  const { user } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  
  // MFA State
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  
  // Usage limits state
  const [usageLimits, setUsageLimits] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);

  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchMFAStatus();
    fetchUsageLimits();
  }, []);

  const fetchMFAStatus = async () => {
    try {
      const response = await api.get(`/auth/mfa/status`);
      setMfaEnabled(response.data.mfa_enabled);
    } catch (error) {
      console.error('Failed to fetch MFA status:', error);
    } finally {
      setMfaLoading(false);
    }
  };

  const fetchUsageLimits = async () => {
    try {
      const response = await api.get(`/auth/usage-limits`);
      setUsageLimits(response.data);
    } catch (error) {
      console.error('Failed to fetch usage limits:', error);
    } finally {
      setUsageLoading(false);
    }
  };

  const handleSetupMFA = async () => {
    try {
      const response = await api.post(`/auth/mfa/setup`);
      setMfaSetupData(response.data);
      setSetupDialogOpen(true);
    } catch (error) {
      toast.error('Failed to start MFA setup');
    }
  };

  const handleVerifyMFA = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setVerifying(true);
    try {
      await api.post(`/auth/mfa/verify`, { code: verifyCode });
      toast.success('MFA enabled successfully!');
      setMfaEnabled(true);
      setSetupDialogOpen(false);
      setVerifyCode('');
      setMfaSetupData(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };

  const handleDisableMFA = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setVerifying(true);
    try {
      await api.post(`/auth/mfa/disable`, { code: verifyCode });
      toast.success('MFA disabled');
      setMfaEnabled(false);
      setDisableDialogOpen(false);
      setVerifyCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed successfully');
      setPasswordDialogOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const getUsagePercentage = (current, max) => {
    if (!max) return 0;
    return Math.min((current / max) * 100, 100);
  };

  const getUsageColor = (current, max) => {
    if (!max) return 'bg-blue-500';
    const pct = (current / max) * 100;
    if (pct >= 90) return 'bg-red-500';
    if (pct >= 70) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  return (
    <div data-testid="settings-page" className="space-y-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <SettingsIcon className="w-7 h-7 text-foreground" />
        <h1 className="text-3xl font-bold text-foreground tracking-tight">Settings</h1>
      </div>

      {/* Account Information */}
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-5 h-5 text-blue-500" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
              <span className="text-xl font-semibold text-white">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-foreground text-lg">{user?.name}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="text-xs">
                  {user?.is_admin ? 'Administrator' : 'User'}
                </Badge>
                {user?.calibration_access && (
                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                    Calibration Access
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Key className="w-5 h-5 text-amber-500" />
            Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">Change Password</p>
              <p className="text-sm text-muted-foreground">Update your account password</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPasswordDialogOpen(true)}
              data-testid="change-password-btn"
            >
              <Key className="w-4 h-4 mr-2" />
              Change
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Usage Limits (for non-admin users) */}
      {!user?.is_admin && usageLimits?.limits && (
        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-500" />
              Usage & Limits
            </CardTitle>
            <CardDescription>
              Your organization's resource usage
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {usageLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* Populations */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">Populations</span>
                    </div>
                    <span className="text-muted-foreground">
                      {usageLimits.usage?.populations || 0}
                      {usageLimits.limits?.max_populations ? ` / ${usageLimits.limits.max_populations}` : ' (Unlimited)'}
                    </span>
                  </div>
                  {usageLimits.limits?.max_populations && (
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${getUsageColor(usageLimits.usage?.populations, usageLimits.limits?.max_populations)}`}
                        style={{ width: `${getUsagePercentage(usageLimits.usage?.populations, usageLimits.limits?.max_populations)}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* Stimuli */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">Stimuli</span>
                    </div>
                    <span className="text-muted-foreground">
                      {usageLimits.usage?.stimuli || 0}
                      {usageLimits.limits?.max_stimuli ? ` / ${usageLimits.limits.max_stimuli}` : ' (Unlimited)'}
                    </span>
                  </div>
                  {usageLimits.limits?.max_stimuli && (
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${getUsageColor(usageLimits.usage?.stimuli, usageLimits.limits?.max_stimuli)}`}
                        style={{ width: `${getUsagePercentage(usageLimits.usage?.stimuli, usageLimits.limits?.max_stimuli)}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* Simulations */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Map className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">Simulations</span>
                    </div>
                    <span className="text-muted-foreground">
                      {usageLimits.usage?.simulations || 0}
                      {usageLimits.limits?.max_simulations ? ` / ${usageLimits.limits.max_simulations}` : ' (Unlimited)'}
                    </span>
                  </div>
                  {usageLimits.limits?.max_simulations && (
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${getUsageColor(usageLimits.usage?.simulations, usageLimits.limits?.max_simulations)}`}
                        style={{ width: `${getUsagePercentage(usageLimits.usage?.simulations, usageLimits.limits?.max_simulations)}%` }}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Appearance */}
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Palette className="w-5 h-5 text-blue-500" />
            Appearance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                isDark ? 'bg-slate-800' : 'bg-amber-100'
              }`}>
                {isDark ? (
                  <Moon className="w-6 h-6 text-blue-400" />
                ) : (
                  <Sun className="w-6 h-6 text-amber-500" />
                )}
              </div>
              <div>
                <p className="font-medium text-foreground">Theme</p>
                <p className="text-sm text-muted-foreground">
                  {isDark ? 'Dark mode is active' : 'Light mode is active'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Sun className="w-4 h-4 text-muted-foreground" />
              <Switch
                checked={isDark}
                onCheckedChange={toggleTheme}
                data-testid="theme-toggle"
              />
              <Moon className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security - MFA */}
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-500" />
            Security
          </CardTitle>
          <CardDescription>
            Two-factor authentication adds an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {mfaLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  mfaEnabled ? 'bg-emerald-100' : 'bg-slate-100 dark:bg-slate-800'
                }`}>
                  <Smartphone className={`w-6 h-6 ${mfaEnabled ? 'text-emerald-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className="font-medium text-foreground">Two-Factor Authentication (2FA)</p>
                  <p className="text-sm text-muted-foreground">
                    {mfaEnabled 
                      ? 'Enabled - Your account is protected with an authenticator app' 
                      : 'Disabled - Add extra security with an authenticator app'
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {mfaEnabled ? (
                  <>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Enabled
                    </Badge>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setDisableDialogOpen(true)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Disable
                    </Button>
                  </>
                ) : (
                  <Button 
                    onClick={handleSetupMFA}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="setup-mfa-btn"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Enable 2FA
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* MFA Setup Dialog */}
      <Dialog open={setupDialogOpen} onOpenChange={setSetupDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-emerald-600" />
              Set Up Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </DialogDescription>
          </DialogHeader>
          
          {mfaSetupData && (
            <div className="space-y-6 mt-4">
              {/* QR Code */}
              <div className="flex justify-center">
                <div className="p-4 bg-white dark:bg-card rounded-lg shadow-sm">
                  <img 
                    src={mfaSetupData.qr_code} 
                    alt="MFA QR Code" 
                    className="w-48 h-48"
                  />
                </div>
              </div>
              
              {/* Manual Entry Key */}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">
                  Or enter this key manually:
                </Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-secondary rounded text-sm font-mono break-all">
                    {mfaSetupData.manual_entry_key}
                  </code>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(mfaSetupData.manual_entry_key)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Verification */}
              <div className="space-y-2">
                <Label htmlFor="verify-code">Enter the 6-digit code from your app</Label>
                <Input
                  id="verify-code"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                  data-testid="mfa-verify-input"
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setSetupDialogOpen(false);
                    setVerifyCode('');
                    setMfaSetupData(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleVerifyMFA}
                  disabled={verifyCode.length !== 6 || verifying}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="verify-mfa-btn"
                >
                  {verifying ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Verify & Enable
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* MFA Disable Dialog */}
      <Dialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Enter your current 2FA code to disable two-factor authentication. 
              This will make your account less secure.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="disable-code">Enter your 6-digit code</Label>
              <Input
                id="disable-code"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center text-2xl tracking-widest font-mono"
                maxLength={6}
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setDisableDialogOpen(false);
                  setVerifyCode('');
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDisableMFA}
                disabled={verifyCode.length !== 6 || verifying}
                variant="destructive"
              >
                {verifying ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Disable 2FA
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={(open) => {
        setPasswordDialogOpen(open);
        if (!open) { setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Change Password
            </DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setPasswordDialogOpen(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                data-testid="submit-password-change-btn"
              >
                {changingPassword ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Change Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
